/**
 * US phone number helpers.
 *
 * A US (NANP) phone number is 10 national digits: a 3-digit area code plus a
 * 7-digit number (e.g. 555 555 5555). Area codes never start with "1", so a
 * leading "1" is always the country/trunk code and is always safe to strip.
 *
 * Two layers, kept separate on purpose:
 *  - DATA  (cleanUsPhone): the canonical value we store/send. Strips a leading 1
 *    and caps at 10. Used ONLY at validation + submit. Never on every keystroke.
 *  - DISPLAY (formatUsPhoneInput): what the user sees while typing. Keeps a
 *    leading 1 VISIBLE as a "+1 " prefix instead of swallowing the keypress, and
 *    formats the 10 national digits. No leading 1 means no prefix.
 *
 * Why two layers: stripping the 1 in the input handler eats the keystroke (on a
 * phone the user presses "1" and nothing happens). The display shows whatever
 * they typed; the normalizer produces the clean 10-digit value only when the
 * data leaves the field.
 *
 * USA only. Do not generalize to international numbers.
 */

// DATA: String -> digits, drop a leading country-code "1", cap at 10 digits.
export function cleanUsPhone(value) {
  let digits = String(value == null ? '' : value).replace(/\D/g, '');
  if (digits.charAt(0) === '1') {
    digits = digits.slice(1);
  }
  return digits.slice(0, 10);
}

// DISPLAY: keep a leading 1 visible as "+1 " and format the national digits.
export function formatUsPhoneInput(value) {
  let digits = String(value == null ? '' : value).replace(/\D/g, '');
  const hasCountryCode = digits.charAt(0) === '1';
  if (hasCountryCode) digits = digits.slice(1);
  const national = digits.slice(0, 10);
  const prefix = hasCountryCode ? '+1 ' : '';
  if (!national) return hasCountryCode ? '+1' : '';
  if (national.length < 4) return `${prefix}${national}`;
  if (national.length < 7) return `${prefix}(${national.slice(0, 3)}) ${national.slice(3)}`;
  return `${prefix}(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6, 10)}`;
}

// For a split area-code box (3-separate-box layouts that can't show a "+1 "
// prefix): let a lone "1" stay visible so the keypress isn't "dead", then drop
// it the moment the next digit is typed. US area codes never start with 1, so
// the 1 was always the country code.
export function cleanAreaCodeBox(value) {
  const digits = String(value == null ? '' : value).replace(/\D/g, '');
  return digits.length > 1 && digits.charAt(0) === '1' ? digits.slice(1) : digits;
}

// A field is valid once it resolves to exactly 10 national digits AND the area
// code starts 2-9. NANP area codes never start with 0 or 1, so a number whose
// area code starts with 1 (e.g. a stray second leading "1": 1 (155) 555-5555)
// is rejected as invalid.
export function isValidUsPhone(value) {
  return /^[2-9]\d{9}$/.test(cleanUsPhone(value));
}

// Call this at the end of a phone @input handler whose field is bound with
// :value (or a computed v-model), passing the value the handler decided to keep.
//
// Vue only writes an input's DOM value when the bound expression CHANGES. The
// browser has already put the typed character into the field by the time the
// handler runs, so when the formatter rejects that character the model comes out
// identical to what it was, Vue patches nothing, and the rejected character stays
// on screen. That is what put "(234) 234-3433433" on the /glp1 checkout while we
// held the correct "(234) 234-3433": past 10 digits every extra key formats back
// to the same string, so nothing re-renders and the extras pile up.
//
// A field bound with a plain-string v-model does NOT need this. v-model writes
// the raw dirty value into the model first, which is a real change, so Vue
// re-renders and its own patch puts the accepted value back. Measured on the
// three-box funnels: they heal themselves.
//
// Writing the accepted value straight onto the element removes the dependency on
// Vue's patch. Only when it differs: assigning an identical string still moves
// the caret to the end, which would break editing in the middle of a number.
export function syncPhoneInput(event, accepted) {
  const el = event && event.target;
  if (el && el.value !== accepted) {
    el.value = accepted;
  }
}
